/**
 * ============================================================
 * KEJJA OS — src/pages/meals/ImportRecipeModal.tsx
 * Import a recipe from a URL, pasted text, or PDF.
 * ============================================================
 */

import { useState, useRef, type ChangeEvent } from 'react';
import {
  X, Globe, FileText, FileUp, Sparkles, Check,
  Heart, ChefHat, AlertCircle,
} from 'lucide-react';

import { api } from '@/lib/api';
import { useCreateRecipe } from '@/hooks/useMeals';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

interface ParsedDraft {
  name: string;
  description?: string | null;
  servings?: number;
  prepMinutes?: number | null;
  cookMinutes?: number | null;
  tags?: string[];
  ingredients: Array<{
    name: string;
    quantity?: number | null;
    unit?: string | null;
    note?: string | null;
  }>;
  steps: Array<{ title: string; body: string; timerSeconds?: number }>;
  method?: string | null;
  source?: { kind: string; url?: string; jsonLd: boolean };
}

type Mode = 'url' | 'paste' | 'pdf';

interface Props {
  homeId: string;
  onClose: () => void;
}

export default function ImportRecipeModal({ homeId, onClose }: Props) {
  const [mode, setMode] = useState<Mode>('url');
  const [url, setUrl] = useState('');
  const [text, setText] = useState('');
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [isFavorite, setIsFavorite] = useState(false);

  const [isParsing, setIsParsing] = useState(false);
  const [parseErr, setParseErr] = useState<string | null>(null);
  const [draft, setDraft] = useState<ParsedDraft | null>(null);

  const createMut = useCreateRecipe(homeId);
  const fileRef = useRef<HTMLInputElement>(null);

  const handleParse = async () => {
    setParseErr(null);
    setDraft(null);
    setIsParsing(true);
    try {
      const payload: Record<string, unknown> = {};
      if (mode === 'url') {
        if (!url.trim()) { setParseErr('Enter a URL.'); setIsParsing(false); return; }
        payload.url = url.trim();
      } else if (mode === 'paste') {
        if (!text.trim()) { setParseErr('Paste some text.'); setIsParsing(false); return; }
        payload.text = text;
      } else if (mode === 'pdf') {
        if (!pdfFile) { setParseErr('Pick a PDF.'); setIsParsing(false); return; }
        const base64 = await fileToBase64(pdfFile);
        payload.pdfBase64 = base64;
      }

      const result = await api.post<{ draft: ParsedDraft }>(
        `/homes/${homeId}/meals/recipes/parse`, payload,
      );
      setDraft(result.draft);
    } catch (err) {
      setParseErr(err instanceof Error ? err.message : 'Parse failed');
    } finally {
      setIsParsing(false);
    }
  };

  const handleSave = () => {
    if (!draft) return;
    createMut.mutate({
      name: draft.name,
      description: draft.description ?? null,
      servings: draft.servings ?? 4,
      prepMinutes: draft.prepMinutes ?? null,
      cookMinutes: draft.cookMinutes ?? null,
      tags: draft.tags ?? [],
      isFavorite,
      method: draft.method ?? null,
      ingredients: draft.ingredients.map((ing) => ({
        name: ing.name,
        quantity: ing.quantity ?? null,
        unit: ing.unit ?? null,
        note: ing.note ?? null,
      })),
      steps: draft.steps,
    }, {
      onSuccess: onClose,
      onError: (e) => setParseErr(e.message ?? 'Could not save'),
    });
  };

  const updateDraft = (patch: Partial<ParsedDraft>) => {
    if (!draft) return;
    setDraft({ ...draft, ...patch });
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <Card className="w-full max-w-2xl my-8 max-h-[calc(100vh-4rem)] overflow-y-auto">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-amber-600" />
              Import recipe
            </h2>
            <Button type="button" size="sm" variant="ghost" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>

          {!draft ? (
            <>
              {/* Mode picker */}
              <div className="flex gap-2">
                <ModeButton icon={Globe} label="URL" active={mode === 'url'} onClick={() => setMode('url')} />
                <ModeButton icon={FileText} label="Paste text" active={mode === 'paste'} onClick={() => setMode('paste')} />
                <ModeButton icon={FileUp} label="Upload PDF" active={mode === 'pdf'} onClick={() => setMode('pdf')} />
              </div>

              {/* Input */}
              {mode === 'url' && (
                <div>
                  <Label className="text-xs">Recipe URL</Label>
                  <Input
                    autoFocus value={url}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setUrl(e.target.value)}
                    placeholder="https://www.foodnetwork.com/recipes/..."
                    className="mt-1"
                  />
                  <p className="text-[10px] text-muted-foreground mt-1">
                    Works best with recipe sites that use standard Schema.org markup
                    (AllRecipes, NYT Cooking, Food Network, Bon Appétit, Serious Eats…).
                  </p>
                </div>
              )}

              {mode === 'paste' && (
                <div>
                  <Label className="text-xs">Paste recipe text</Label>
                  <textarea
                    autoFocus value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder="Paste ingredients + instructions from anywhere…"
                    className="w-full mt-1 min-h-[200px] rounded-md border border-border bg-background p-2 text-xs font-mono"
                  />
                  <p className="text-[10px] text-muted-foreground mt-1">
                    We try to detect ingredient lists and numbered steps.
                    You'll review + edit before saving.
                  </p>
                </div>
              )}

              {mode === 'pdf' && (
                <div>
                  <Label className="text-xs">Upload PDF</Label>
                  <div className="mt-1 flex gap-2 items-center">
                    <Button
                      variant="outline" size="sm"
                      onClick={() => fileRef.current?.click()}
                    >
                      <FileUp className="w-3.5 h-3.5 mr-1" />
                      {pdfFile ? 'Change file' : 'Pick PDF'}
                    </Button>
                    {pdfFile && (
                      <span className="text-xs text-muted-foreground truncate">
                        {pdfFile.name} · {(pdfFile.size / 1024).toFixed(0)} KB
                      </span>
                    )}
                    <input
                      ref={fileRef} type="file" accept="application/pdf"
                      className="hidden"
                      onChange={(e) => setPdfFile(e.target.files?.[0] ?? null)}
                    />
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-1">
                    Text-based PDFs only (not scanned images).
                    Max 10 MB for imports.
                  </p>
                </div>
              )}

              {parseErr && (
                <div className="flex items-start gap-2 text-xs text-rose-600 bg-rose-50 dark:bg-rose-950/20 border border-rose-200 dark:border-rose-900/40 rounded-md p-2">
                  <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                  <span>{parseErr}</span>
                </div>
              )}

              <div className="flex gap-2 pt-2 border-t border-border">
                <Button
                  onClick={handleParse}
                  disabled={isParsing}
                  className="bg-amber-600 hover:bg-amber-700 text-white flex-1"
                >
                  {isParsing ? 'Parsing…' : 'Parse recipe'}
                </Button>
                <Button variant="ghost" onClick={onClose}>Cancel</Button>
              </div>
            </>
          ) : (
            <DraftReview
              draft={draft}
              isFavorite={isFavorite}
              setIsFavorite={setIsFavorite}
              onChange={updateDraft}
              onBack={() => setDraft(null)}
              onSave={handleSave}
              isSaving={createMut.isPending}
              err={parseErr}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function ModeButton({
  icon: Icon, label, active, onClick,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg border text-xs font-medium transition-colors',
        active
          ? 'border-amber-500/60 bg-amber-500/10 text-amber-700 dark:text-amber-300'
          : 'border-border text-muted-foreground',
      )}
    >
      <Icon className="w-3.5 h-3.5" />
      {label}
    </button>
  );
}

// ============================================================
// Draft review form — edit before save
// ============================================================

function DraftReview({
  draft, isFavorite, setIsFavorite, onChange, onBack, onSave, isSaving, err,
}: {
  draft: ParsedDraft;
  isFavorite: boolean;
  setIsFavorite: (v: boolean) => void;
  onChange: (patch: Partial<ParsedDraft>) => void;
  onBack: () => void;
  onSave: () => void;
  isSaving: boolean;
  err: string | null;
}) {
  return (
    <>
      <div className="flex items-center gap-2 p-2.5 rounded-md bg-emerald-500/10 border border-emerald-500/30 text-xs">
        <Check className="w-3.5 h-3.5 text-emerald-700 dark:text-emerald-300 flex-shrink-0" />
        <span className="text-emerald-700 dark:text-emerald-300">
          {draft.source?.jsonLd
            ? 'Parsed from structured recipe markup — usually very accurate.'
            : 'Parsed with heuristics. Double-check and edit before saving.'}
        </span>
      </div>

      {/* Basics */}
      <div className="grid grid-cols-[1fr_auto] gap-2">
        <div>
          <Label className="text-xs">Name</Label>
          <Input
            value={draft.name}
            onChange={(e) => onChange({ name: e.target.value })}
            className="mt-1"
          />
        </div>
        <div>
          <Label className="text-xs">Fav</Label>
          <Button
            type="button" variant={isFavorite ? 'default' : 'outline'}
            className={cn('mt-1 h-9 w-9 p-0', isFavorite && 'bg-rose-600 hover:bg-rose-700 text-white')}
            onClick={() => setIsFavorite(!isFavorite)}
          >
            <Heart className={cn('w-4 h-4', isFavorite && 'fill-current')} />
          </Button>
        </div>
      </div>

      {draft.description && (
        <div>
          <Label className="text-xs">Description</Label>
          <Input
            value={draft.description}
            onChange={(e) => onChange({ description: e.target.value })}
            className="mt-1"
          />
        </div>
      )}

      <div className="grid grid-cols-3 gap-2">
        <div>
          <Label className="text-xs">Servings</Label>
          <Input
            type="number" min="1"
            value={draft.servings ?? 4}
            onChange={(e) => onChange({ servings: parseInt(e.target.value, 10) || 4 })}
            className="mt-1"
          />
        </div>
        <div>
          <Label className="text-xs">Prep (min)</Label>
          <Input
            type="number" min="0"
            value={draft.prepMinutes ?? ''}
            onChange={(e) => onChange({ prepMinutes: e.target.value ? parseInt(e.target.value, 10) : null })}
            className="mt-1"
          />
        </div>
        <div>
          <Label className="text-xs">Cook (min)</Label>
          <Input
            type="number" min="0"
            value={draft.cookMinutes ?? ''}
            onChange={(e) => onChange({ cookMinutes: e.target.value ? parseInt(e.target.value, 10) : null })}
            className="mt-1"
          />
        </div>
      </div>

      {/* Ingredients */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <Label className="text-xs">Ingredients ({draft.ingredients.length})</Label>
        </div>
        <div className="rounded-md border border-border bg-muted/20 p-2 max-h-48 overflow-y-auto space-y-1">
          {draft.ingredients.map((ing, i) => (
            <div key={i} className="text-xs flex items-center gap-2">
              <span className="tabular-nums text-muted-foreground w-20 flex-shrink-0 truncate">
                {ing.quantity ?? ''}{ing.unit ? ` ${ing.unit}` : ''}
              </span>
              <span className="flex-1 truncate">{ing.name}</span>
              {ing.note && <Badge variant="outline" className="text-[9px]">{ing.note}</Badge>}
            </div>
          ))}
          {draft.ingredients.length === 0 && (
            <p className="text-[11px] text-muted-foreground italic text-center py-2">No ingredients detected.</p>
          )}
        </div>
      </div>

      {/* Steps */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <Label className="text-xs">Steps ({draft.steps.length})</Label>
        </div>
        <div className="rounded-md border border-border bg-muted/20 p-2 max-h-64 overflow-y-auto space-y-2">
          {draft.steps.map((s, i) => (
            <div key={i} className="text-xs flex gap-2">
              <div className="h-5 w-5 rounded-full bg-amber-100 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 text-[10px] font-bold flex items-center justify-center flex-shrink-0">
                {i + 1}
              </div>
              <div className="flex-1 min-w-0">
                {s.title && <div className="font-semibold truncate">{s.title}</div>}
                <div className="text-muted-foreground text-[11px] line-clamp-2">{s.body}</div>
              </div>
            </div>
          ))}
          {draft.steps.length === 0 && (
            <p className="text-[11px] text-muted-foreground italic text-center py-2">No steps detected — parsing fell back to notes.</p>
          )}
        </div>
      </div>

      {draft.tags && draft.tags.length > 0 && (
        <div className="flex gap-1 flex-wrap">
          {draft.tags.map((t) => (
            <Badge key={t} variant="outline" className="text-[10px]">{t}</Badge>
          ))}
        </div>
      )}

      <p className="text-[10px] text-muted-foreground italic">
        Save now, then edit any detail in the recipe library.
      </p>

      {err && (
        <div className="text-xs text-rose-600 font-medium">{err}</div>
      )}

      <div className="flex gap-2 pt-2 border-t border-border">
        <Button
          onClick={onSave}
          disabled={isSaving || !draft.name}
          className="bg-amber-600 hover:bg-amber-700 text-white flex-1"
        >
          <ChefHat className="w-4 h-4 mr-1" />
          {isSaving ? 'Saving…' : 'Save recipe'}
        </Button>
        <Button variant="outline" onClick={onBack}>← Back</Button>
      </div>
    </>
  );
}

// ============================================================
// Helpers
// ============================================================

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') { reject(new Error('bad_read')); return; }
      // strip "data:application/pdf;base64,"
      const commaIdx = result.indexOf(',');
      resolve(commaIdx >= 0 ? result.slice(commaIdx + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error('read_error'));
    reader.readAsDataURL(file);
  });
}
